import React from 'react';
import { Grid, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { displayLongSentences } from '../../../utils/utils';
import SingleUser from '../components/SingleUser';
import { IChatList, SenderType } from '../../../redux/chat/chatSlice';
import { AssetType } from '../../../redux/activity/activitySlice';
import { getTimeDifference } from '../../../utils/dateUtils';

interface IStyledGridProps {
  isselectedchat?: boolean;
  hasNewMessages?: boolean;
}

export const StyledGrid = styled(Grid)<IStyledGridProps>(({ isselectedchat, hasNewMessages }) => {
  let backgroundColor = '#FFF';
  let hoverBackgroundColor =
    'linear-gradient(0deg, rgba(124, 174, 190, 0.20) 0%, rgba(124, 174, 190, 0.20) 100%), #FFF';

  if (isselectedchat) {
    backgroundColor = '#FFDDC8';
    hoverBackgroundColor = '#FFDDC8';
  } else if (hasNewMessages) {
    backgroundColor = 'rgba(99, 139, 152, 0.15)';
  }

  return {
    display: 'flex',
    gap: '12px',
    padding: '16px',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(124, 174, 190, 0.50)',
    backgroundColor,
    '&:hover': {
      background: hoverBackgroundColor,
    },
  };
});

interface IConversationProps {
  chat: IChatList;
  isselectedChat: boolean;
}

function Conversation({ chat, isselectedChat }: IConversationProps) {
  const { t } = useTranslation();

  const hasNewMessages = chat.chatMessages?.some(
    (msg) => !msg.messageSeen && msg.senderType === SenderType.CLIENT
  );

  return (
    <StyledGrid isselectedchat={isselectedChat} hasNewMessages={hasNewMessages} key={chat.id}>
      <SingleUser
        profilePic={chat.client?.profilePic || ''}
        firstName={chat.client?.firstName || ''}
        lastName={chat.client?.lastName || ''}
        hideStatus
      />
      <Grid container direction="column" gap="4px">
        <Grid item container justifyContent="space-between" alignItems="center">
          <Grid gap={1} display="flex" flexDirection="row">
            <Typography variant="calloutRegular" color="primary">
              {chat.client?.firstName || ''} {chat.client?.lastName || ''}
            </Typography>
            {chat.client?.archived && <img src="/assets/icons/archive.svg" alt="archive" />}
          </Grid>

          <Typography variant="caption2Bold" color="#7CAEBE" fontWeight={400}>
            {getTimeDifference(chat.updatedAt)}
          </Typography>
        </Grid>
        {chat.chatMessages &&
          chat.chatMessages.length !== 0 &&
          chat.chatMessages[0].messageType === AssetType.TEXT && (
            <Typography variant="meta" color="primary">
              {displayLongSentences(chat.chatMessages && chat.chatMessages[0].message, 75)}
            </Typography>
          )}
        {chat.chatMessages &&
          chat.chatMessages.length !== 0 &&
          chat.chatMessages[0].messageType === AssetType.IMAGE && (
            <Grid display="flex" flexDirection="row" gap="4px" alignItems="center">
              <img src="/assets/icons/photo.svg" alt="message" height={20} width={20} />
              <Typography variant="meta" color="primary">
                {t('chat.photo')}
              </Typography>
            </Grid>
          )}
        {chat.chatMessages &&
          chat.chatMessages.length !== 0 &&
          chat.chatMessages[0].messageType === AssetType.VIDEO && (
            <Grid display="flex" flexDirection="row" gap="4px" alignItems="center">
              <img src="/assets/icons/video-camera.svg" alt="message" height={24} width={24} />
              <Typography variant="meta" color="primary">
                {t('chat.video')}
              </Typography>
            </Grid>
          )}
        {chat.chatMessages &&
          chat.chatMessages.length !== 0 &&
          chat.chatMessages[0].messageType === AssetType.AUDIO && (
            <Grid display="flex" flexDirection="row" gap="4px" alignItems="center">
              <img src="/assets/icons/voice.svg" alt="message" height={24} width={24} />
              <Typography variant="meta" color="primary">
                {t('chat.audio')}
              </Typography>
            </Grid>
          )}
        {chat.chatMessages &&
          chat.chatMessages.length !== 0 &&
          chat.chatMessages[0].messageType === AssetType.DOCUMENT && (
            <Grid display="flex" flexDirection="row" gap="4px" alignItems="center">
              <img src="/assets/icons/file-text.svg" alt="message" height={20} width={20} />
              <Typography variant="meta" color="primary">
                {t('chat.document')}
              </Typography>
            </Grid>
          )}
      </Grid>
    </StyledGrid>
  );
}

export default Conversation;
